import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Heart, ShieldCheck, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

const NATURES = [
  { value: "bereavement", label: "Bereavement / funeral support" },
  { value: "illness", label: "Illness or hospital costs" },
  { value: "loss_of_income", label: "Sudden loss of income" },
  { value: "immigration", label: "Immigration crisis" },
  { value: "housing", label: "Housing emergency" },
  { value: "other", label: "Other — describe below" },
];

/**
 * Karuṇā Fund — confidential community hardship grant application.
 * Mirrors the dignity-first language we'd want any neighbour to read.
 */
export default function KarunaFund() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", location: "",
    nature: "bereavement", description: "", amount_requested_gbp: "",
    consent_to_contact: true,
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const body = { ...form };
      if (body.amount_requested_gbp === "") body.amount_requested_gbp = null;
      else body.amount_requested_gbp = parseFloat(body.amount_requested_gbp);
      const r = await api.post("/karuna-fund/apply", body);
      setSubmitted(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "We couldn't submit your application — please try again, or email info@ambedkaritebuddhistglobalnetworkuk.com directly.");
    }
    setBusy(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-pearl py-16 px-4" data-testid="karuna-fund-thank-you">
        <div className="max-w-2xl mx-auto bg-white border border-navy/10 rounded-sm p-10 text-center">
          <CheckCircle2 size={48} className="mx-auto" style={{ color: "#8A2D3B" }} />
          <h1 className="mt-4 font-display text-3xl text-navy">Received in confidence</h1>
          <p className="mt-3 text-slate-700">{submitted.message}</p>
          <p className="mt-2 text-sm text-slate-500">
            Your reference is{" "}
            <code className="px-2 py-0.5 bg-pearl border border-navy/10 rounded text-saffron font-mono text-xs">
              {submitted.reference}
            </code>
            . Save this — quote it in any correspondence.
          </p>
          <p className="mt-6 text-xs text-slate-500 italic">
            If your situation is immediately life-threatening, please call 999 or the
            Samaritans free on 116 123. We are here to help, but we are not an emergency service.
          </p>
          <Link to="/" className="mt-8 inline-flex items-center gap-1 text-saffron text-sm hover:underline">
            <ArrowLeft size={14} /> Back to the website
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pearl" data-testid="karuna-fund-page">
      <header className="py-16 px-4" style={{ background: "linear-gradient(135deg,#0B1F4D 0%,#8A2D3B 100%)" }}>
        <div className="max-w-3xl mx-auto text-pearl">
          <Link to="/" className="inline-flex items-center gap-1 text-saffron text-xs uppercase tracking-widest hover:text-saffron-light">
            <ArrowLeft size={12} /> Back to website
          </Link>
          <div className="mt-5 inline-flex items-center gap-2 text-saffron text-[10px] uppercase tracking-[0.3em]">
            <Heart size={12} /> Compassion in action
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl">The Karuṇā Fund</h1>
          <p className="mt-4 text-pearl/80 text-lg max-w-2xl">
            Confidential hardship grants for community members in urgent need. A trustee will
            respond within 7 working days. You do not need to be a member to apply.
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-10">
        <form onSubmit={submit} className="space-y-5 bg-white border border-navy/10 rounded-sm p-8" data-testid="karuna-fund-form">
          <h2 className="font-display text-2xl text-navy">Your application</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Your full name" required value={form.full_name} onChange={(v) => set("full_name", v)} testid="kf-name" />
            <Field label="Email address" type="email" required value={form.email} onChange={(v) => set("email", v)} testid="kf-email" />
            <Field label="Phone (optional)" value={form.phone} onChange={(v) => set("phone", v)} testid="kf-phone" />
            <Field label="Town / city (optional)" value={form.location} onChange={(v) => set("location", v)} testid="kf-location" />
          </div>

          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Nature of need</span>
            <select
              value={form.nature}
              onChange={(e) => set("nature", e.target.value)}
              data-testid="kf-nature"
              className="w-full border border-navy/15 rounded-sm px-3 py-2.5 text-sm"
            >
              {NATURES.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Please describe your situation (in confidence)</span>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              required
              minLength={20} maxLength={4000}
              rows={6}
              placeholder="Share as much or as little as feels right. Trustees treat every application with discretion."
              data-testid="kf-description"
              className="w-full border border-navy/15 rounded-sm px-3 py-2.5 text-sm leading-relaxed"
            />
            <p className="mt-1 text-[11px] text-slate-400">{form.description.length} / 4000 characters</p>
          </label>

          <Field
            label="Amount you are requesting (£, optional)"
            type="number" step="50"
            value={form.amount_requested_gbp}
            onChange={(v) => set("amount_requested_gbp", v)}
            placeholder="e.g. 250"
            testid="kf-amount"
          />

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.consent_to_contact}
              onChange={(e) => set("consent_to_contact", e.target.checked)}
              data-testid="kf-consent"
              className="mt-1"
            />
            <span>
              I consent to a trustee contacting me by email or phone to discuss this application.
              I understand the trustees will keep my application confidential.
            </span>
          </label>

          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="kf-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="kf-submit"
            className="inline-flex items-center gap-2 px-6 py-3 text-pearl rounded-sm font-medium hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: "#8A2D3B" }}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Heart size={16} />}
            {busy ? "Submitting…" : "Submit application in confidence"}
          </button>
        </form>

        <aside className="space-y-4 text-sm">
          <div className="bg-white border border-navy/10 rounded-sm p-5">
            <h3 className="font-display text-lg text-navy flex items-center gap-2">
              <Lock size={16} className="text-saffron" /> Confidential
            </h3>
            <p className="mt-2 text-slate-600 text-[13px] leading-relaxed">
              Only the trustees handling Karuṇā Fund decisions read these applications.
              We do not share them with members or publish identifying details.
            </p>
          </div>
          <div className="bg-white border border-navy/10 rounded-sm p-5">
            <h3 className="font-display text-lg text-navy flex items-center gap-2">
              <ShieldCheck size={16} className="text-saffron" /> Safeguarding
            </h3>
            <p className="mt-2 text-slate-600 text-[13px] leading-relaxed">
              If your situation involves a child at risk, you can reach our Designated
              Safeguarding Lead by emailing{" "}
              <a href="mailto:safeguarding@ambedkaritebuddhistglobalnetworkuk.com" className="text-saffron underline">
                safeguarding@ambedkaritebuddhistglobalnetworkuk.com
              </a>.
            </p>
          </div>
          <div className="text-xs text-slate-500 italic px-2">
            Karuṇā · Pāli for &ldquo;active compassion&rdquo;. 100% of donations to the Karuṇā Fund
            go directly to community members. Administration is paid for separately by the Trustees.
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid, step }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-slate-500 mb-1">{label}</span>
      <input
        type={type} step={step}
        required={required}
        placeholder={placeholder}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-navy/15 rounded-sm px-3 py-2.5 text-sm"
      />
    </label>
  );
}
